import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams } from 'react-router'

import { Err, Ok, Result } from 'ts-results'

import { useNotifications } from 'app/application/application'
import { ConnectionSetupRoutes } from 'app/application/types'
import { ConnectionIcon } from 'app/common/presentation'
import { InnkeeprIcon } from 'app/common/presentation/icons'
import { useConnections } from 'app/connections/application'
import { isInstalledConnection } from 'app/connections/types/connections'

import { Button } from 'components'

import { AuthorizationButton, type AuthorizationData } from '../../authorization'
import { useConnection } from '../../components/ConnectionContext'
import styles from '../Connect.module.scss'
import { ConnectionStepper } from './components/ConnectionStepper/ConnectionStepper'

type SignInParams = { name: string }

const SignIn = () => {
  const connection = useConnection()
  const { saveAuthorization } = useConnections()
  const navigate = useNavigate()
  const { createNotification, te } = useNotifications()
  const { t } = useTranslation('connections')

  const { name } = useParams<SignInParams>() as SignInParams
  if (!connection) return <Navigate to={`/connections/${name}`} replace />
  if (!connection.multiStepSetup) return <Navigate to={`/connections/${name}/connect`} replace />

  const handleAuthorizationSuccess = async (
    authorizationData: AuthorizationData,
  ): Promise<Result<void, string | void>> =>
    saveAuthorization(
      connection,
      connection.config,
      authorizationData.authorizationCode,
      authorizationData.redirectUri,
    ).then((result) => {
      if (result.ok) return Ok(undefined)

      createNotification({
        message: result.val.message,
        title: te(`http.${result.val.code}`),
        type: 'error',
      })
      return Err(result.val.message)
    })

  const connectionName = t(`available.${connection.name}.displayName`)
  const handleContinue = () => {
    if (isInstalledConnection(connection)) {
      void navigate(`../${ConnectionSetupRoutes.SelectAccounts}`, {
        state: { connection },
      })
    }
  }

  return (
    <>
      <ConnectionStepper activeStep={1} />

      <section className={styles.signInCard}>
        <header>
          <h2>{t('setup.signIn.title', { connection: connectionName })}</h2>
          <p>{t('setup.signIn.description', { connection: connectionName })}</p>
        </header>
        <div className={styles.signInContent}>
          <div className={styles.platforms}>
            <div className={styles.platformIcon}>
              <InnkeeprIcon />
            </div>
            <div className={styles.connectionLine} />
            <div className={styles.platformIcon}>
              <ConnectionIcon connection={connection} />
            </div>
          </div>
          {connection.authConfig && (
            <AuthorizationButton
              connection={connection}
              onAuthorizationSuccess={handleAuthorizationSuccess}
            />
          )}
          <p className={styles.authorizationHint}>
            {t('setup.signIn.hint', { connection: connectionName })}
          </p>
        </div>
      </section>

      <div className={styles.actions}>
        <Button onClick={() => void navigate(-1)} variant="secondary">
          {t('setup.actions.back')}
        </Button>
        <Button disabled={!isInstalledConnection(connection)} onClick={handleContinue}>
          {t('setup.signIn.continue')}
        </Button>
      </div>
    </>
  )
}

export default SignIn
